// Per-resource cache freshness, replacing a single global TTL.
//
// A 2-minute staleTime applied uniformly is wrong in both directions: the role
// list barely changes in a week, while a task board changes while you look at
// it. These tiers name the intent so a hook declares what KIND of data it holds
// rather than picking a number.
//
// gcTime is deliberately much longer than staleTime everywhere. staleTime
// decides when to REVALIDATE; gcTime decides when to FORGET. Keeping data past
// its freshness window is what lets a revisited screen paint instantly and
// refresh behind the scenes, instead of showing a spinner.

const SECOND = 1000;
const MINUTE = 60 * SECOND;

export const CACHE_POLICY = {
  /**
   * The inherited default for hooks that do not declare a tier.
   *
   * Kept at the pre-existing 2 minutes ON PURPOSE. Most queries in the app
   * inherit this — project detail, members, tasks, notes, forms, documents,
   * team, roles — and web additionally has refetchOnWindowFocus enabled, so
   * shortening it would make every tab focus refetch the screen twice as often.
   * That is the opposite of the goal. Only gcTime was raised (see below).
   */
  default: {
    staleTime: 2 * MINUTE,
    gcTime: 30 * MINUTE,
  },

  /**
   * Reference data that changes only when an admin edits it: roles, permission
   * catalogues, project types, the user directory. Wrong-for-10-minutes here is
   * invisible, and mutations invalidate these keys explicitly anyway.
   */
  static: {
    staleTime: 10 * MINUTE,
    gcTime: 60 * MINUTE,
  },

  /**
   * Slowly-changing data a person edits occasionally: project metadata, team
   * membership, saved views, templates.
   */
  reference: {
    staleTime: 5 * MINUTE,
    gcTime: 30 * MINUTE,
  },

  /**
   * The working set — tasks, notes, documents, project detail. Changes during a
   * session, often by someone else, so revalidate promptly. Still served from
   * cache first: `stale` means "refresh in background", not "hide".
   */
  operational: {
    staleTime: 60 * SECOND,
    gcTime: 30 * MINUTE,
  },

  /**
   * Data whose whole value is being current: notifications, dashboard panels,
   * unread counts.
   */
  live: {
    staleTime: 30 * SECOND,
    gcTime: 15 * MINUTE,
  },
} as const;

/**
 * Poll intervals. Every polled query must also pause while the page is hidden —
 * TanStack's default `refetchIntervalInBackground: false` handles that, so do
 * not set it to true.
 */
export const POLL_INTERVAL = {
  dashboard: 60 * SECOND,
  analytics: 5 * MINUTE,
  // Notifications intentionally absent: NOTIFICATION_REFETCH_MS already lives in
  // @task-tracker/shared and both platforms import it, which is a stronger
  // guarantee of web/mobile parity than duplicating the number here.
} as const;

/**
 * Retry policy.
 *
 * The old blanket `retry: 1` retried everything, including 401/403/404 — none
 * of which get better on a second attempt. Each pointless retry costs a full
 * ~258 ms Worker→Supabase round-trip and doubles the time the user spends
 * looking at a spinner before the error finally appears.
 *
 * 408 and 429 are the exceptions: both are explicitly "try again".
 */
const RETRYABLE_CLIENT_STATUSES = new Set([408, 429]);

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;

  const status = (error as { status?: unknown })?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return RETRYABLE_CLIENT_STATUSES.has(status);
  }

  // Network failures and 5xx are worth one more attempt.
  return true;
}
